import { useState, useEffect } from "react";
import { useApi } from "./AuthContext";
import { Settings, Save, RotateCcw } from "lucide-react";
import { toast } from "sonner";

const DEFAULT_PRICING = {
  property_sizes: { up_to_1_acre: 60, "1_to_2_acres": 100, "2_to_3_acres": 140, "3_to_4_acres": 180, above_5_acres: 0 },
  shrub_trimming: { none: 0, front_only: 40, front_1_side: 60, front_2_sides: 80, whole_house: 100 },
  extra_shrub_price: 10,
};

const PROP_LABELS = { up_to_1_acre: "Up to 1 Acre", "1_to_2_acres": "1-2 Acres", "2_to_3_acres": "2-3 Acres", "3_to_4_acres": "3-4 Acres", above_5_acres: "5+ Acres (Contact)" };
const SHRUB_LABELS = { none: "None", front_only: "Front Only", front_1_side: "Front + 1 Side", front_2_sides: "Front + 2 Sides", whole_house: "Whole House" };

export default function PricingModule({ readOnly = false }) {
  const api = useApi();
  const [pricing, setPricing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/admin/pricing");
        setPricing(data);
      } catch (err) {
        setPricing(DEFAULT_PRICING);
      } finally { setLoading(false); }
    })();
  }, []);

  const updateProp = (key, value) => {
    setPricing(p => ({ ...p, property_sizes: { ...p.property_sizes, [key]: parseFloat(value) || 0 } }));
  };
  const updateShrub = (key, value) => {
    setPricing(p => ({ ...p, shrub_trimming: { ...p.shrub_trimming, [key]: parseFloat(value) || 0 } }));
  };
  const updateExtra = (value) => {
    setPricing(p => ({ ...p, extra_shrub_price: parseFloat(value) || 0 }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put("/admin/pricing", pricing);
      toast.success("Pricing updated! Changes are live immediately.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save");
    } finally { setSaving(false); }
  };

  const handleReset = () => {
    if (!window.confirm("Reset all prices to defaults?")) return;
    setPricing(DEFAULT_PRICING);
  };

  if (loading || !pricing) return <div className="flex items-center justify-center h-64 text-stone-500">Loading...</div>;

  return (
    <div data-testid="pricing-module" className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-forest-50 rounded-lg flex items-center justify-center"><Settings className="w-5 h-5 text-forest-600" /></div>
          <div>
            <h2 className="text-lg font-semibold text-stone-800">Pricing Configuration</h2>
            <p className="text-sm text-stone-500">Set the base prices for the estimate calculator</p>
          </div>
        </div>
        {!readOnly && <div className="flex gap-2">
          <button onClick={handleReset} className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm border border-stone-300 text-stone-600 hover:bg-stone-50"><RotateCcw className="w-4 h-4" /> Reset</button>
          <button data-testid="save-pricing-btn" onClick={handleSave} disabled={saving} className="flex items-center gap-1.5 bg-forest-600 hover:bg-forest-700 text-white px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-50">
            <Save className="w-4 h-4" /> {saving ? "Saving..." : "Save Changes"}
          </button>
        </div>}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Property Sizes */}
        <div className="bg-white rounded-xl border border-stone-200 p-6 shadow-sm">
          <h3 className="font-semibold text-stone-800 mb-4">Property Size Pricing</h3>
          <div className="space-y-3">
            {Object.entries(PROP_LABELS).map(([key, label]) => (
              <div key={key} className="flex items-center justify-between gap-3">
                <label className="text-sm text-stone-600 flex-1">{label}</label>
                <div className="relative w-24">
                  <span className="absolute left-2 top-1/2 -translate-y-1/2 text-stone-400 text-sm">$</span>
                  <input
                    data-testid={`price-prop-${key}`}
                    type="number" min="0" step="5"
                    value={pricing.property_sizes?.[key] || 0}
                    onChange={(e) => updateProp(key, e.target.value)}
                    className="w-full pl-6 pr-2 py-1.5 border border-stone-300 rounded-lg text-sm text-right focus:ring-2 focus:ring-forest-500"
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Shrub Trimming */}
        <div className="bg-white rounded-xl border border-stone-200 p-6 shadow-sm">
          <h3 className="font-semibold text-stone-800 mb-4">Shrub Trimming Pricing</h3>
          <div className="space-y-3">
            {Object.entries(SHRUB_LABELS).map(([key, label]) => (
              <div key={key} className="flex items-center justify-between gap-3">
                <label className="text-sm text-stone-600 flex-1">{label}</label>
                <div className="relative w-24">
                  <span className="absolute left-2 top-1/2 -translate-y-1/2 text-stone-400 text-sm">$</span>
                  <input
                    data-testid={`price-shrub-${key}`}
                    type="number" min="0" step="5"
                    value={pricing.shrub_trimming?.[key] || 0}
                    onChange={(e) => updateShrub(key, e.target.value)}
                    className="w-full pl-6 pr-2 py-1.5 border border-stone-300 rounded-lg text-sm text-right focus:ring-2 focus:ring-forest-500"
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Extra Shrubs */}
        <div className="bg-white rounded-xl border border-stone-200 p-6 shadow-sm">
          <h3 className="font-semibold text-stone-800 mb-4">Extra Shrubs</h3>
          <div className="flex items-center justify-between gap-3">
            <label className="text-sm text-stone-600">Price per extra shrub</label>
            <div className="relative w-24">
              <span className="absolute left-2 top-1/2 -translate-y-1/2 text-stone-400 text-sm">$</span>
              <input
                data-testid="price-extra-shrub"
                type="number" min="0" step="1"
                value={pricing.extra_shrub_price || 0}
                onChange={(e) => updateExtra(e.target.value)}
                className="w-full pl-6 pr-2 py-1.5 border border-stone-300 rounded-lg text-sm text-right focus:ring-2 focus:ring-forest-500"
              />
            </div>
          </div>
          <div className="mt-6 p-4 bg-forest-50 rounded-lg">
            <p className="text-sm text-forest-700"><strong>How it works:</strong> The estimate calculator on the landing page uses these prices. Changes are reflected immediately.</p>
          </div>
        </div>
      </div>
    </div>
  );
}
